import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenuItem,
  Label,
  Textarea,
} from '@cold-ambulance/shadcn';
import {
  DownloadStoredFile,
  ViewStoredFile,
  useAction,
  useData,
} from '@cold-ambulance/ui';

function RequestDocumentDialog({
  customerId,
  fileId,
  name,
}: {
  customerId: string;
  fileId: string | null | undefined;
  name: string;
}) {
  if (!fileId) return null;

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const requestDocument = useAction('POST /customer/{id}/request-documents', {
    meta: { toast: true },
    onSuccess: () => toast.success('Document request sent'),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          Request update
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Request document update</DialogTitle>
          <DialogDescription>
            Send a reason to request a fresh copy of <strong>{name}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reason">Reason</Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain what is missing or invalid"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await requestDocument.mutateAsync({
                id: customerId,
                fileId,
                name,
                reason,
              });
              setOpen(false);
              setReason('');
            }}
            disabled={!reason.trim() || requestDocument.isPending}
          >
            {requestDocument.isPending ? 'Sending...' : 'Send request'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ViewCustomerDocumentsDialog({
  customerId,
}: {
  customerId: string;
}) {
  const [open, setOpen] = useState(false);

  const documentsQuery = useData(
    'GET /customer/{id}/documents',
    { id: customerId },
    { enabled: open },
  );

  const data = documentsQuery.data;

  const documentMap = useMemo(() => {
    const map = new Map<string, { id: string; needsAttention: boolean }>();
    data?.documents?.forEach((doc) => {
      map.set(doc.name, { id: doc.id, needsAttention: doc.needsAttention });
    });
    return map;
  }, [data]);

  const fields = useMemo(
    () =>
      [
        { key: 'commercialRegistrationFile', label: 'Commercial Registration' },
        { key: 'bankCertificate', label: 'Bank Certificate' },
        { key: 'taxCertificate', label: 'Tax Certificate' },
        { key: 'zakaCertificate', label: 'Zakat Certificate' },
        {
          key: 'nationalAddressCertificate',
          label: 'National Address Certificate',
        },
        { key: 'companyLogo', label: 'Company Logo' },
      ] as const,
    [],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          View documents
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Customer Documents</DialogTitle>
          <DialogDescription>
            Uploaded documents for this customer.
          </DialogDescription>
        </DialogHeader>
        {documentsQuery.isLoading ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            Loading documents...
          </div>
        ) : data ? (
          <div className="space-y-4">
            <div className="divide-y rounded-md border">
              {fields.map((field) => {
                const doc = documentMap.get(field.key);
                const fileId = doc?.id;
                const needsAttention = doc?.needsAttention || false;
                return (
                  <div
                    key={field.key}
                    className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">{field.label}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">
                          {field.label || 'Not uploaded'}
                        </span>
                        <Badge
                          variant={
                            !fileId
                              ? 'destructive'
                              : needsAttention
                                ? 'secondary'
                                : 'default'
                          }
                        >
                          {!fileId
                            ? 'MISSING'
                            : needsAttention
                              ? 'PENDING'
                              : 'RESOLVED'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ViewStoredFile fileId={fileId} />
                      <DownloadStoredFile fileId={fileId} />
                      <RequestDocumentDialog
                        customerId={customerId}
                        fileId={fileId}
                        name={field.key}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-destructive py-6 text-center text-sm">
            Unable to load documents.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
